import {DataType, QGiQueryType} from "../types/enums";
import {
  Correlation,
  Correlations,
  FigureResult,
  LibraryModalResult,
  QueryModalResult,
  Score,
  Scores
} from "../types/list";

import React, {createContext, ReactNode, useCallback, useContext, useState} from "react";

interface ListState {
  dataType: DataType;
  queryType: QGiQueryType;
  displayedTableData: Scores | Correlations | null;
  selectedRowModal: Score | Correlation | null;
  libraryModalResult: LibraryModalResult | null;
  queryModalResult: QueryModalResult | LibraryModalResult | null;
  showFigureModal: FigureResult | undefined;
}

interface ListContextType {
  listState: ListState;
  setDataType: (dataType: DataType) => void;
  setQueryType: (queryType: QGiQueryType) => void;
  setDisplayedTableData: (displayedTableData: Scores | Correlations | null) => void;
  setSelectedRowModal: (selectedRow: Score | Correlation | null) => void;
  setLibraryModalResult: (libraryModalResult: LibraryModalResult | null) => void;
  setQueryModalResult: (queryModalResult: QueryModalResult | LibraryModalResult | null) => void;
  setShowFigureModal: (figureResult: FigureResult | undefined) => void;
}

const ListContext = createContext<ListContextType | undefined>(undefined);

interface ListProviderProps {
  children: ReactNode;
}

export const ListProvider: React.FC<ListProviderProps> = ({children}) => {
  const [listState, setListState] = useState<ListState>({
    dataType: DataType.Correlations,
    queryType: QGiQueryType.All,
    displayedTableData: null,
    selectedRowModal: null,
    libraryModalResult: null,
    queryModalResult: null,
    showFigureModal: undefined,
  });

  const setDataType = useCallback((dataType: DataType) => {
    setListState(prev => ({...prev, dataType: dataType}));
  }, []);

  const setQueryType = useCallback((queryType: QGiQueryType) => {
    setListState(prev => ({...prev, queryType: queryType}));
  }, []);

  const setDisplayedTableData = useCallback((displayedTableData: Scores | Correlations | null) => {
    setListState(prev => ({...prev, displayedTableData: displayedTableData}));
  }, []);

  const setSelectedRowModal = useCallback((selectedRow: Score | Correlation | null) => {
    setListState(prev => ({ ...prev, selectedRowModal: selectedRow }));
  }, []);

  const setLibraryModalResult = useCallback((libraryModalResult: LibraryModalResult | null) => {
    setListState(prev => ({...prev, libraryModalResult: libraryModalResult}));
  }, []);

  const setQueryModalResult = useCallback((queryModalResult: QueryModalResult | LibraryModalResult | null) => {
    setListState(prev => ({...prev, queryModalResult: queryModalResult}));
  }, []);

  const setShowFigureModal = useCallback((showFigureModal: FigureResult | undefined) => {
    setListState(prev => ({...prev, showFigureModal: showFigureModal}));
  }, []);

  return (
    <ListContext.Provider
      value={{
        listState,
        setDataType,
        setQueryType,
        setDisplayedTableData,
        setSelectedRowModal,
        setLibraryModalResult,
        setQueryModalResult,
        setShowFigureModal,
      }}
    >
      {children}
    </ListContext.Provider>
  );
};

export const useListContext = () => {
  const context = useContext(ListContext);
  if (!context) {
    throw new Error('useListContext must be used within an ListProvider');
  }
  return context;
};
